/**
 * API Chat instantané — /api/messagerie-chat/
 *
 * Toutes les mutations invalident les tags pour rafraîchir la sidebar auto.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ─── Types ──────────────────────────────────────────────────────────────────

export type TypeConv = 'direct' | 'groupe' | 'service' | 'broadcast';
export type Statut = 'online' | 'busy' | 'meeting' | 'away' | 'offline';

export interface ConversationItem {
  id: number;
  type_conv: TypeConv;
  libelle: string;
  titre_affichage: string;
  description: string;
  avatar_url: string | null;
  couleur: string;
  date_derniere_activite: string;
  nb_participants: number;
  participants_apercu: { user_id: number; user_nom: string; role_conv: string }[];
  nb_non_lus: number;
  epingle: boolean;
  archive: boolean;
  muted: boolean;
  suis_admin: boolean;
  dernier_message: MessageItem | null;
}

export interface ReactionAgregee {
  emoji: string;
  users: { user_id: number; user_nom: string }[];
  moi: boolean;
}

export interface MessageItem {
  id: number;
  conversation_id: number;
  expediteur_user_id: number;
  expediteur_nom: string;
  expediteur_role: string;
  a_moi: boolean;
  type_msg: 'texte' | 'image' | 'fichier' | 'systeme';
  contenu: string;
  date_envoi: string;
  modifie: boolean;
  epingle: boolean;
  important: boolean;
  supprime?: boolean;
  fichier_url?: string;
  fichier_nom?: string;
  fichier_taille?: number;
  fichier_mime?: string;
  reply_to?: {
    id: number;
    expediteur_nom: string;
    contenu: string;
    type_msg: string;
  };
  reactions?: ReactionAgregee[];
  lu_par?: number[];
  /** IDs des destinataires ayant reçu (mais pas nécessairement lu) le message.
   *  Utilisé pour les indicateurs style WhatsApp : ✓ envoyé / ✓✓ livré / ✓✓ bleu lu. */
  livre_a?: number[];
}

export interface ContactItem {
  user_id: number;
  username: string;
  nom: string;
  role: string;
  role_label: string;
  email: string;
  telephone: string;
  photo_url: string | null;
  statut: Statut;
  en_ligne: boolean;
  activite: string;
  dernier_ping: string | null;
}

export interface PresenceItem {
  user_id: number;
  user_nom: string;
  user_role: string;
  role_label: string;
  statut: Statut;
  page_courante: string;
  activite: string;
  dernier_ping: string | null;
}

// ─── API ────────────────────────────────────────────────────────────────────

export const messagerieChatApi = createApi({
  reducerPath: 'messagerieChatApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['ChatConvs', 'ChatMessages', 'ChatContacts', 'ChatPresence', 'ChatRappels'],
  endpoints: (b) => ({
    listerConversations: b.query<{ results: ConversationItem[]; total: number }, { q?: string; archive?: boolean } | void>({
      query: (params) => ({
        url: '/messagerie-chat/conversations/',
        params: params ? {
          ...(params.q ? { q: params.q } : {}),
          ...(params.archive ? { archive: '1' } : {}),
        } : undefined,
      }),
      providesTags: ['ChatConvs'],
    }),
    detailConversation: b.query<ConversationItem, number>({
      query: (id) => `/messagerie-chat/conversations/${id}/`,
      providesTags: (_r, _e, id) => [{ type: 'ChatConvs', id }],
    }),
    creerConversation: b.mutation<ConversationItem, {
      type_conv: TypeConv;
      libelle?: string;
      description?: string;
      participants_user_ids: number[];
    }>({
      query: (body) => ({
        url: '/messagerie-chat/conversations/',
        method: 'POST', body,
      }),
      invalidatesTags: ['ChatConvs'],
    }),
    patcherConversation: b.mutation<ConversationItem, { id: number; body: any }>({
      query: ({ id, body }) => ({
        url: `/messagerie-chat/conversations/${id}/`,
        method: 'PATCH', body,
      }),
      invalidatesTags: ['ChatConvs'],
    }),
    quitterConversation: b.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/messagerie-chat/conversations/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ChatConvs'],
    }),
    listerMessages: b.query<{ results: MessageItem[]; total: number; a_plus: boolean }, { conv_id: number; avant_id?: number; limit?: number }>({
      query: ({ conv_id, avant_id, limit }) => ({
        url: `/messagerie-chat/conversations/${conv_id}/messages/`,
        params: {
          ...(avant_id ? { avant_id } : {}),
          ...(limit ? { limit } : {}),
        },
      }),
      providesTags: (_r, _e, { conv_id }) => [{ type: 'ChatMessages', id: conv_id }],
    }),
    envoyerMessage: b.mutation<MessageItem, {
      conv_id: number;
      contenu?: string;
      reply_to_id?: number;
      important?: boolean;
      fichier?: File;
    }>({
      query: ({ conv_id, contenu, reply_to_id, important, fichier }) => {
        // Multipart si fichier
        if (fichier) {
          const fd = new FormData();
          if (contenu) fd.append('contenu', contenu);
          if (reply_to_id) fd.append('reply_to_id', String(reply_to_id));
          if (important) fd.append('important', 'true');
          fd.append('fichier', fichier);
          return {
            url: `/messagerie-chat/conversations/${conv_id}/messages/`,
            method: 'POST', body: fd,
          };
        }
        return {
          url: `/messagerie-chat/conversations/${conv_id}/messages/`,
          method: 'POST',
          body: { contenu, reply_to_id, important },
        };
      },
      invalidatesTags: (_r, _e, { conv_id }) => [
        { type: 'ChatMessages', id: conv_id },
        'ChatConvs',
      ],
    }),
    marquerLus: b.mutation<{ success: boolean; nb_non_lus: number }, number>({
      query: (conv_id) => ({
        url: `/messagerie-chat/conversations/${conv_id}/marquer-lus/`,
        method: 'POST',
      }),
      invalidatesTags: ['ChatConvs'],
    }),
    supprimerMessage: b.mutation<{ success: boolean }, number>({
      query: (msg_id) => ({
        url: `/messagerie-chat/messages/${msg_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ChatMessages'],
    }),
    editerMessage: b.mutation<MessageItem, { msg_id: number; contenu: string }>({
      query: ({ msg_id, contenu }) => ({
        url: `/messagerie-chat/messages/${msg_id}/`,
        method: 'PATCH', body: { contenu },
      }),
      invalidatesTags: ['ChatMessages'],
    }),
    toggleReaction: b.mutation<{ success: boolean; action: 'ajoute' | 'retire' }, { msg_id: number; emoji: string }>({
      query: ({ msg_id, emoji }) => ({
        url: `/messagerie-chat/messages/${msg_id}/reactions/`,
        method: 'POST', body: { emoji },
      }),
      invalidatesTags: ['ChatMessages'],
    }),
    epinglerMessage: b.mutation<{ success: boolean; epingle: boolean }, number>({
      query: (msg_id) => ({
        url: `/messagerie-chat/messages/${msg_id}/epingler/`,
        method: 'POST',
      }),
      invalidatesTags: ['ChatMessages'],
    }),
    listerContacts: b.query<{ results: ContactItem[]; total: number; stats_par_role: Record<string, number> }, { role?: string; q?: string } | void>({
      query: (params) => ({
        url: '/messagerie-chat/contacts/',
        params: params || undefined,
      }),
      providesTags: ['ChatContacts'],
    }),
    ouvrirDirect: b.mutation<ConversationItem, { user_id: number }>({
      query: (body) => ({
        url: '/messagerie-chat/contacts/ouvrir-direct/',
        method: 'POST', body,
      }),
      invalidatesTags: ['ChatConvs'],
    }),
    ping: b.mutation<{ ok: boolean; statut_effectif: Statut; server_time: string }, { page: string; activite: string }>({
      query: (body) => ({
        url: '/messagerie-chat/presence/ping/',
        method: 'POST', body,
      }),
    }),
    listerPresence: b.query<{
      results: PresenceItem[]; total: number; mon_statut: Statut;
      visible_annuaire?: boolean;
      // Fix 2026-09-02 : présent + true quand le user est masqué et donc
      // volontairement privé de la liste des connectés (règle de réciprocité).
      invisible?: boolean;
      detail?: string;
    }, { roles?: string; role?: string } | void>({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params?.roles) usp.set('roles', params.roles);
        else if (params?.role) usp.set('role', params.role);
        const qs = usp.toString();
        return `/messagerie-chat/presence/${qs ? '?' + qs : ''}`;
      },
      providesTags: ['ChatPresence'],
    }),
    changerStatut: b.mutation<{ ok: boolean; statut: Statut }, { statut: Statut }>({
      query: (body) => ({
        url: '/messagerie-chat/statut/',
        method: 'POST', body,
      }),
      invalidatesTags: ['ChatPresence'],
    }),
    changerVisibilite: b.mutation<{ ok: boolean; visible_annuaire: boolean }, { visible: boolean }>({
      query: (body) => ({
        url: '/messagerie-chat/visibilite/',
        method: 'POST', body,
      }),
      invalidatesTags: ['ChatPresence'],
    }),
    listerRappels: b.query<{ results: any[] }, void>({
      query: () => '/messagerie-chat/rappels/',
      providesTags: ['ChatRappels'],
    }),
    creerRappel: b.mutation<{ id: number; date_rappel: string }, {
      contenu: string; delai: string;
      conversation_id?: number; message_source_id?: number;
    }>({
      query: (body) => ({
        url: '/messagerie-chat/rappels/',
        method: 'POST', body,
      }),
      invalidatesTags: ['ChatRappels'],
    }),
    rechercher: b.query<{ results: any[]; q: string; total: number }, string>({
      query: (q) => ({
        url: '/messagerie-chat/recherche/',
        params: { q },
      }),
    }),
    broadcastUrgent: b.mutation<{ ok: boolean; conversation_id: number; nb_destinataires: number }, {
      contenu: string; niveau: 'info' | 'important' | 'urgent'; cibles: 'tous' | string[];
    }>({
      query: (body) => ({
        url: '/messagerie-chat/broadcast/',
        method: 'POST', body,
      }),
      invalidatesTags: ['ChatConvs'],
    }),

    // ── Typing indicator (léger, pas de WS) ─────────────────────────────
    signalerTyping: b.mutation<{ ok: boolean }, number>({
      query: (conv_id) => ({
        url: `/messagerie-chat/conversations/${conv_id}/typing/`,
        method: 'POST',
      }),
    }),
    typingStatus: b.query<{ typing: { user_id: number; user_nom: string }[] }, number>({
      query: (conv_id) => `/messagerie-chat/conversations/${conv_id}/typing-status/`,
    }),

    // ── Transfert de message ────────────────────────────────────────────
    transfererMessage: b.mutation<
      { forwarded: { conv_id: number; message_id: number }[]; errors: any[] },
      { msg_id: number; conv_ids: number[] }
    >({
      query: ({ msg_id, conv_ids }) => ({
        url: `/messagerie-chat/messages/${msg_id}/transferer/`,
        method: 'POST',
        body: { conv_ids },
      }),
      invalidatesTags: ['ChatConvs', 'ChatMessages'],
    }),

    // ── Groupes de classe (communauté étudiante) ────────────────────────
    maClasseGroupe: b.query<{
      eligible: boolean;
      raison?: string;
      classe?: string;
      annee?: string;
      libelle?: string;
      existe?: boolean;
      conv_id?: number | null;
      membre?: boolean;
      nb_membres?: number;
    }, void>({
      query: () => '/messagerie-chat/groupes-classe/ma-classe/',
      providesTags: ['ChatConvs'],
    }),
    rejoindreGroupeClasse: b.mutation<{
      ok: boolean; created: boolean; conv_id: number; libelle: string;
      classe: string; annee: string; nb_membres: number; nb_camarades_ajoutes: number;
    }, void>({
      query: () => ({
        url: '/messagerie-chat/groupes-classe/rejoindre/',
        method: 'POST',
      }),
      invalidatesTags: ['ChatConvs'],
    }),
  }),
});

export const {
  useListerConversationsQuery,
  useDetailConversationQuery,
  useCreerConversationMutation,
  usePatcherConversationMutation,
  useQuitterConversationMutation,
  useListerMessagesQuery,
  useEnvoyerMessageMutation,
  useMarquerLusMutation,
  useSupprimerMessageMutation,
  useEditerMessageMutation,
  useToggleReactionMutation,
  useEpinglerMessageMutation,
  useListerContactsQuery,
  useOuvrirDirectMutation,
  usePingMutation,
  useListerPresenceQuery,
  useChangerStatutMutation,
  useChangerVisibiliteMutation,
  useListerRappelsQuery,
  useCreerRappelMutation,
  useRechercherQuery,
  useLazyRechercherQuery,
  useBroadcastUrgentMutation,
  useMaClasseGroupeQuery,
  useRejoindreGroupeClasseMutation,
  useSignalerTypingMutation,
  useTypingStatusQuery,
  useTransfererMessageMutation,
} = messagerieChatApi;
